import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  GraduationCap,
  Video,
  FileText,
  Image,
  AlertTriangle,
  Upload,
  Move,
  Globe,
  Clock,
  Calendar,
  Loader2,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Palette,
} from "lucide-react";
import {
  getAllEducationContent,
  createEducationContent,
  updateEducationContent,
  deleteEducationContent,
  togglePublishStatus,
  reorderEducationContent,
  getYouTubeVideoId,
  getYouTubeThumbnail,
  clearEducationCache,
  uploadThumbnailImage,
  deleteThumbnailImage,
} from "../../lib/educationService";
import { useAuth } from "../../context/AuthContext";
import Header from "../Header";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import ConfirmationModal from "../ui/ConfirmationModal";
import { toast, Toaster } from "sonner";

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const colors = [
    "#ffffff",
    "#f8fafc",
    "#f1f5f9",
    "#e2e8f0",
    "#cbd5e1",
    "#94a3b8",
    "#64748b",
    "#475569",
    "#334155",
    "#1e293b",
    "#0f172a",
    "#000000",
    "#B8460E",
    "#8B3A0E",
    "#dc2626",
    "#ea580c",
    "#d97706",
    "#ca8a04",
    "#65a30d",
    "#16a34a",
    "#059669",
    "#0891b2",
    "#0284c7",
    "#2563eb",
    "#4f46e5",
    "#7c3aed",
    "#a21caf",
    "#be185d",
    "#f59e0b",
    "#10b981",
    "#ef4444",
    "#8b5cf6",
    "#f97316",
    "#06b6d4",
    "#84cc16",
    "#ec4899",
  ];

  const fontSizes = [
    { label: "Small", command: "1" },
    { label: "Normal", command: "3" },
    { label: "Large", command: "4" },
    { label: "XL", command: "5" },
    { label: "XXL", command: "6" },
    { label: "XXXL", command: "7" },
  ];

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);

    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const applyColor = (color) => {
    execCommand("foreColor", color);
    setShowColorPicker(false);
  };

  const applyBackgroundColor = (color) => {
    execCommand("hiliteColor", color);
    setShowBackgroundPicker(false);
  };

  const applyFontSize = (size) => {
    execCommand("fontSize", size);
  };

  const insertImage = async (file) => {
    setIsUploadingImage(true);
    try {
      const uploadResult = await uploadThumbnailImage(file);
      if (uploadResult.success) {
        // Store current selection/cursor position
        const selection = window.getSelection();
        let range = null;

        if (selection.rangeCount > 0) {
          range = selection.getRangeAt(0);
        } else {
          // If no selection, create range at end of editor
          range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        }

        // Create image element
        const img = document.createElement("img");
        img.src = uploadResult.url;
        img.alt = "Content image";
        img.style.cssText =
          "max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0; display: block;";

        // Insert image at cursor position
        range.deleteContents();
        range.insertNode(img);

        // Move cursor after image
        range.setStartAfter(img);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        // Focus back to editor
        editorRef.current.focus();

        // Update content
        onChange(editorRef.current.innerHTML);
      } else {
        toast.error(`Failed to upload image: ${uploadResult.error}`);
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(`Error uploading image: ${error.message}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, WebP, or GIF)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must be less than 5MB");
      return;
    }

    insertImage(file);
  };

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="bg-black/20 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-white/10 bg-white/5">
        {/* Font Size */}
        <div className="flex items-center gap-1">
          <select
            onChange={(e) => applyFontSize(e.target.value)}
            className="bg-black/40 border border-white/20 text-white text-sm rounded px-2 py-1 hover:bg-black/60 transition-colors"
            title="Font Size"
          >
            <option value="">Size</option>
            {fontSizes.map((size) => (
              <option key={size.command} value={size.command}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        <div className="w-px h-6 bg-white/10"></div>

        {/* Text formatting */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => execCommand("bold")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Bold"
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("italic")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Italic"
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("underline")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Underline"
          >
            <Underline className="h-4 w-4" />
          </button>
        </div>

        <div className="w-px h-6 bg-white/10"></div>

        {/* Lists */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => execCommand("insertUnorderedList")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("insertOrderedList")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </button>
        </div>

        <div className="w-px h-6 bg-white/10"></div>

        {/* Text Color picker */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Text Color"
          >
            <Palette className="h-4 w-4" />
          </button>

          {showColorPicker && (
            <div className="absolute top-full left-0 mt-2 p-3 bg-black/80 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl z-50">
              <p className="text-white text-xs mb-2">Text Color</p>
              <div className="grid grid-cols-6 gap-2 w-60">
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => applyColor(color)}
                    className="w-6 h-6 rounded border border-white/20 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Background Color picker */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowBackgroundPicker(!showBackgroundPicker)}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Background Color"
          >
            <div className="h-4 w-4 border border-white/40 rounded flex items-center justify-center">
              <div className="h-2 w-2 bg-current rounded"></div>
            </div>
          </button>

          {showBackgroundPicker && (
            <div className="absolute top-full left-0 mt-2 p-3 bg-black/80 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl z-50">
              <p className="text-white text-xs mb-2">Background Color</p>
              <div className="grid grid-cols-6 gap-2 w-60">
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => applyBackgroundColor(color)}
                    className="w-6 h-6 rounded border border-white/20 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-white/10"></div>

        {/* Alignment */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => execCommand("justifyLeft")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Align Left"
          >
            <div className="h-4 w-4 flex flex-col gap-0.5">
              <div className="h-0.5 bg-current w-full"></div>
              <div className="h-0.5 bg-current w-3/4"></div>
              <div className="h-0.5 bg-current w-full"></div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => execCommand("justifyCenter")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Align Center"
          >
            <div className="h-4 w-4 flex flex-col gap-0.5 items-center">
              <div className="h-0.5 bg-current w-3/4"></div>
              <div className="h-0.5 bg-current w-1/2"></div>
              <div className="h-0.5 bg-current w-3/4"></div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => execCommand("justifyRight")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Align Right"
          >
            <div className="h-4 w-4 flex flex-col gap-0.5 items-end">
              <div className="h-0.5 bg-current w-full"></div>
              <div className="h-0.5 bg-current w-3/4"></div>
              <div className="h-0.5 bg-current w-full"></div>
            </div>
          </button>
        </div>

        <div className="w-px h-6 bg-white/10"></div>

        {/* Image Upload */}
        <div className="relative">
          <input
            ref={(input) => (window.imageFileInput = input)}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            disabled={isUploadingImage}
          />
          <button
            type="button"
            onClick={() => {
              if (window.imageFileInput && !isUploadingImage) {
                window.imageFileInput.click();
              }
            }}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            title="Insert Image"
            disabled={isUploadingImage}
          >
            {isUploadingImage ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Image className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Clear Formatting */}
        <div className="w-px h-6 bg-white/10"></div>
        <button
          type="button"
          onClick={() => execCommand("removeFormat")}
          className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
          title="Clear Formatting"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-6 min-h-[400px] text-white focus:outline-none prose prose-invert prose-lg max-w-none"
        data-placeholder={placeholder}
        style={{
          "--tw-prose-body": "rgb(229 231 235)",
          "--tw-prose-headings": "rgb(255 255 255)",
          "--tw-prose-links": "rgb(184 70 14)",
          "--tw-prose-bold": "rgb(255 255 255)",
          "--tw-prose-counters": "rgb(156 163 175)",
          "--tw-prose-bullets": "rgb(156 163 175)",
          "--tw-prose-quotes": "rgb(229 231 235)",
          "--tw-prose-quote-borders": "rgb(75 85 99)",
          "--tw-prose-code": "rgb(229 231 235)",
          "--tw-prose-pre-code": "rgb(229 231 235)",
          "--tw-prose-pre-bg": "rgb(31 41 55)",
          "--tw-prose-th-borders": "rgb(75 85 99)",
          "--tw-prose-td-borders": "rgb(55 65 81)",
          lineHeight: "1.6",
        }}
        suppressContentEditableWarning={true}
      />

      {/* Click outside to close pickers */}
      {(showColorPicker || showBackgroundPicker) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowColorPicker(false);
            setShowBackgroundPicker(false);
          }}
        />
      )}

      {/* Editor styles */}
      <style jsx>{`
        [contenteditable="true"] font[size="1"] {
          font-size: 10px !important;
        }
        [contenteditable="true"] font[size="2"] {
          font-size: 13px !important;
        }
        [contenteditable="true"] font[size="3"] {
          font-size: 16px !important;
        }
        [contenteditable="true"] font[size="4"] {
          font-size: 18px !important;
        }
        [contenteditable="true"] font[size="5"] {
          font-size: 24px !important;
        }
        [contenteditable="true"] font[size="6"] {
          font-size: 32px !important;
        }
        [contenteditable="true"] font[size="7"] {
          font-size: 48px !important;
        }

        [contenteditable="true"] h1 {
          font-size: 2.5rem !important;
          font-weight: bold !important;
          margin: 1.5rem 0 1rem 0 !important;
          color: rgb(255 255 255) !important;
          line-height: 1.2 !important;
          display: block !important;
        }

        [contenteditable="true"] h2 {
          font-size: 2rem !important;
          font-weight: bold !important;
          margin: 1.2rem 0 0.8rem 0 !important;
          color: rgb(255 255 255) !important;
          line-height: 1.3 !important;
          display: block !important;
        }

        [contenteditable="true"] h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
          margin: 1rem 0 0.6rem 0 !important;
          color: rgb(255 255 255) !important;
          line-height: 1.4 !important;
          display: block !important;
        }

        [contenteditable="true"] img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 8px !important;
          margin: 16px 0 !important;
          display: block !important;
        }

        [contenteditable="true"] p {
          margin: 0.5rem 0 !important;
          line-height: 1.6 !important;
        }

        [contenteditable="true"]:empty:before {
          content: attr(data-placeholder);
          color: rgb(156 163 175);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

const EducationManagement = () => {
  const { isAdmin, isRoleLoading } = useAuth();
  const [educationContent, setEducationContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    videoUrl: "",
    thumbnailUrl: "",
    orderIndex: 0,
    isPublished: false,
  });

  // Image upload state
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchEducationContent();
    }
  }, [isAdmin]);

  const fetchEducationContent = async () => {
    console.log("🔄 Fetching education content...");
    setLoading(true);

    try {
      const content = await getAllEducationContent();
      console.log("✅ Education content loaded:", content.length, "items");
      setEducationContent(content);
    } catch (error) {
      console.error("❌ Error fetching education content:", error);
      toast.error(`Error loading education content: ${error.message}`);
      setEducationContent([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      content: "",
      videoUrl: "",
      thumbnailUrl: "",
      orderIndex: educationContent.length,
      isPublished: false,
    });
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setIsUploadingThumbnail(false);
  };

  const handleCreate = () => {
    resetForm();
    setSelectedContent(null);
    setShowCreateModal(true);
  };

  const handleEdit = (content) => {
    setFormData({
      title: content.title,
      description: content.description || "",
      content: content.content || "",
      videoUrl: content.videoUrl || "",
      thumbnailUrl: content.thumbnailUrl || "",
      orderIndex: content.orderIndex,
      isPublished: content.isPublished,
    });
    setSelectedContent(content);
    setThumbnailFile(null);
    setThumbnailPreview(content.thumbnailUrl || null);
    setShowEditModal(true);
  };

  const handleDelete = (content) => {
    setContentToDelete(content);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!contentToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteEducationContent(contentToDelete.id);
      if (result.success) {
        toast.success(
          `Education content "${contentToDelete.title}" deleted successfully.`
        );
        fetchEducationContent();
      } else {
        toast.error(`Failed to delete content: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error deleting content: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setContentToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setIsSubmitting(true);
    try {
      let result;
      let finalFormData = { ...formData };

      // Handle thumbnail upload if new file is selected
      if (thumbnailFile) {
        setIsUploadingThumbnail(true);
        const uploadResult = await uploadThumbnailImage(
          thumbnailFile,
          selectedContent?.id
        );

        if (uploadResult.success) {
          finalFormData.thumbnailUrl = uploadResult.url;

          // Delete old thumbnail if updating existing content
          if (selectedContent && selectedContent.thumbnailUrl) {
            await deleteThumbnailImage(selectedContent.thumbnailUrl);
          }
        } else {
          toast.error(`Failed to upload thumbnail: ${uploadResult.error}`);
          setIsSubmitting(false);
          setIsUploadingThumbnail(false);
          return;
        }
        setIsUploadingThumbnail(false);
      }

      // Auto-generate thumbnail from YouTube video if no custom thumbnail
      if (formData.videoUrl && !finalFormData.thumbnailUrl && !thumbnailFile) {
        const videoId = getYouTubeVideoId(formData.videoUrl);
        if (videoId) {
          finalFormData.thumbnailUrl = getYouTubeThumbnail(videoId);
        }
      }

      if (selectedContent) {
        // Update existing content
        result = await updateEducationContent(
          selectedContent.id,
          finalFormData
        );
        if (result.success) {
          toast.success("Education content updated successfully!");
        }
      } else {
        // Create new content
        result = await createEducationContent(finalFormData);
        if (result.success) {
          toast.success("Education content created successfully!");
        }
      }

      if (result.success) {
        setShowCreateModal(false);
        setShowEditModal(false);
        fetchEducationContent();
        resetForm();
      } else {
        toast.error(`Failed to save content: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error saving content: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setIsUploadingThumbnail(false);
    }
  };

  const handleTogglePublish = async (content) => {
    try {
      const result = await togglePublishStatus(
        content.id,
        !content.isPublished
      );
      if (result.success) {
        toast.success(
          `Content ${!content.isPublished ? "published" : "unpublished"} successfully!`
        );
        fetchEducationContent();
      } else {
        toast.error(`Failed to update status: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error updating status: ${error.message}`);
    }
  };

  // Handle thumbnail file selection
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, WebP, or GIF)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must be less than 5MB");
      return;
    }

    setThumbnailFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setThumbnailPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove thumbnail
  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setFormData({ ...formData, thumbnailUrl: "" });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Show loading state while checking admin status
  if (isRoleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-gray-300">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show permission error only after loading is complete
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white p-8 flex items-center justify-center">
        <Card className="text-center p-8 bg-black/40 border border-red-500/50">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-6">
            You do not have permission to view this page.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      <Header />

      {/* Sonner Toaster */}
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "white",
          },
        }}
      />

      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#B8460E] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B3A0E] rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container mx-auto px-6 py-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <GraduationCap className="h-8 w-8 text-[#B8460E]" />
                Education Management
              </h1>
              <p className="text-gray-400 mt-2">
                Create and manage educational content for your affiliates
              </p>
            </div>
            <Button
              onClick={handleCreate}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Content
            </Button>
          </div>

          {/* Education Content Grid */}
          {educationContent.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {educationContent.map((content) => (
                <Card
                  key={content.id}
                  className="relative overflow-hidden group hover:shadow-2xl transition-all duration-300"
                >
                  {/* Animated background elements */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute w-32 h-32 bg-[#B8460E]/10 rounded-full blur-2xl animate-pulse"></div>
                  </div>

                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

                  <div className="relative z-10 p-6">
                    {/* Status and type badges */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {content.videoUrl ? (
                          <div className="flex items-center gap-1 text-[#B8460E]">
                            <Video className="h-4 w-4" />
                            <span className="text-xs font-medium">Video</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-blue-400">
                            <FileText className="h-4 w-4" />
                            <span className="text-xs font-medium">Written</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleTogglePublish(content)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            content.isPublished
                              ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              : "bg-gray-500/20 text-gray-400 hover:bg-gray-500/30"
                          }`}
                          title={content.isPublished ? "Published" : "Draft"}
                        >
                          {content.isPublished ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Thumbnail or placeholder */}
                    {content.thumbnailUrl ? (
                      <div className="w-full h-32 bg-black rounded-lg mb-4 overflow-hidden">
                        <img
                          src={content.thumbnailUrl}
                          alt={content.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : content.videoUrl ? (
                      <div className="w-full h-32 bg-gradient-to-br from-[#B8460E]/20 to-[#8B3A0E]/20 rounded-lg mb-4 flex items-center justify-center">
                        <Video className="h-8 w-8 text-[#B8460E]" />
                      </div>
                    ) : (
                      <div className="w-full h-32 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg mb-4 flex items-center justify-center">
                        <FileText className="h-8 w-8 text-blue-400" />
                      </div>
                    )}

                    {/* Content info */}
                    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                      {content.title}
                    </h3>

                    {content.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                        {content.description}
                      </p>
                    )}

                    {/* Meta info */}
                    <div className="text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1 mb-1">
                        <Calendar className="h-3 w-3" />
                        <span>Updated {formatDate(content.updatedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Move className="h-3 w-3" />
                        <span>Order: {content.orderIndex}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(content)}
                        className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/20"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(content)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center p-12">
              <GraduationCap className="w-16 h-16 text-[#B8460E] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">
                No Education Content Yet
              </h3>
              <p className="text-gray-400 mb-6">
                Create your first educational content to help your affiliates
                succeed.
              </p>
              <Button
                onClick={handleCreate}
                className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Content
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">
                {selectedContent
                  ? "Edit Education Content"
                  : "Create Education Content"}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]"
            >
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E]"
                  placeholder="Enter content title..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E] resize-none"
                  rows="3"
                  placeholder="Brief description of the content..."
                />
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <Video className="h-4 w-4 inline mr-1" />
                  YouTube Video URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, videoUrl: e.target.value })
                  }
                  className="w-full bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E]"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <Image className="h-4 w-4 inline mr-1" />
                  Thumbnail Image (Optional)
                </label>

                {/* Image preview */}
                {thumbnailPreview && (
                  <div className="mb-4 relative inline-block">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="w-32 h-24 object-cover rounded-lg border border-white/20"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveThumbnail}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Upload button */}
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleThumbnailChange}
                      className="hidden"
                      disabled={isUploadingThumbnail}
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition-colors">
                      {isUploadingThumbnail ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span className="text-sm">
                        {isUploadingThumbnail ? "Uploading..." : "Choose Image"}
                      </span>
                    </div>
                  </label>

                  {thumbnailFile && (
                    <span className="text-sm text-gray-400">
                      {thumbnailFile.name}
                    </span>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Upload a custom thumbnail or leave empty to auto-generate from
                  YouTube video. Supported formats: JPEG, PNG, WebP, GIF (max
                  5MB)
                </p>
              </div>

              {/* Rich Text Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Content (Optional)
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Write your educational content here. Use the toolbar to format text, add headings, lists, and colors..."
                />
              </div>

              {/* Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.orderIndex}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orderIndex: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E]"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPublished}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isPublished: e.target.checked,
                        })
                      }
                      className="w-5 h-5 rounded border-white/20 bg-black/20 text-[#B8460E] focus:ring-[#B8460E] focus:ring-2"
                    />
                    <span className="font-medium">Publish immediately</span>
                  </label>
                </div>
              </div>

              {/* Submit buttons */}
              <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white flex-1"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {isSubmitting
                    ? "Saving..."
                    : selectedContent
                      ? "Update Content"
                      : "Create Content"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Education Content"
        message={`Are you sure you want to delete "${contentToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default EducationManagement;
